import { useCallback } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { stringify, parse } from 'qs'

/**
 * Set specific query parameter values
 * @callback useQueryParamsSetQuery
 * @param {object} params - Key-value pairs to update the query parameters too
 */
/**
 * Remove specific query parameter values
 * @callback useQueryParamsRemoveQuery
 * @param {...string} keyNames - The names of the parameters to remove from the query string
 */
/**
 * Remove all the query parameters in the URL
 * @callback useQueryParamsClearQuery
 */
/**
 * @typedef {object} useQueryParamsReturnValues
 * @property {object} query - An Object containing the key-value pairs of the search string
 * @property {string} search - The original query string (`?thing=this&otherThing=that`)
 * @property {useQueryParamsSetQuery} setQuery - Set specific query parameter values
 * @property {useQueryParamsRemoveQuery} removeQuery - Remove specific query parameter values
 * @property {useQueryParamsClearQuery} clearQuery - Remove all the query parameters in the URL
 */

// modify a copy of the most recent location data and return updated URL with queryParams
function modQuery({ search, pathname }, modFn, { stringifyOptions, parseOptions }, startEmpty = false) {
  const searchObj = parse(startEmpty ? '' : search.replace(/^\?/, ''), parseOptions)
  modFn(searchObj) // expecting caller to mutate the data
  const queryStr = stringify(searchObj, stringifyOptions)
  return `${pathname}${queryStr ? '?' : ''}${queryStr}`
}

/**
 * Hook to handle the current url query parameters.
 * @param {object} options - Config options
 * @param {object} options.stringifyOptions - qs::stringify config options
 * @param {object} options.parseOptions - qs::parse config options
 * @returns {useQueryParamsReturnValues} - Object containing the parsed query, the original search string, and methods to modify the URL Search
 */
export default function useQueryParams({ stringifyOptions, parseOptions } = {}) {
  const { push } = useHistory()
  const location = useLocation()
  const { search } = location
  const query = parse(search.replace(/^\?/, ''), parseOptions)

  const setQuery = useCallback(
    (params, clearFirst) => {
      const path = modQuery(
        location,
        (newSearchObj) => Object.assign(newSearchObj, params),
        { stringifyOptions, parseOptions },
        clearFirst
      )
      push(path)
    },
    [location, push, stringifyOptions, parseOptions]
  )

  const removeQuery = useCallback(
    (...paramList) => {
      const path = modQuery(location, (newSearchObj) => paramList.forEach((item) => delete newSearchObj[item]), {
        stringifyOptions,
        parseOptions,
      })
      push(path)
    },
    [location, push, stringifyOptions, parseOptions]
  )

  const clearQuery = useCallback(() => {
    push(location.pathname)
  }, [location, push])

  return {
    // getters
    query,
    search,

    // setters
    setQuery,
    removeQuery,
    clearQuery,
  }
}
